import React, { useState } from "react"
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Box,
  Button,
  Paper,
} from "@mui/material"
import { Edit as EditIcon } from "@mui/icons-material"
import KnowledgeBasePanel from "./KnowledgeBasePanel"

const AiAgentDetail = ({ agent, access, closeDetail, onEdit }) => {
  const [kbCount, setKbCount] = useState(0)

  if (!agent) return null

  return (
    <div className="detail-shell">
      <Grid container spacing={3}>
        {/* Agent Information */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box className="detail-toolbar">
                <Typography variant="h4" component="h1">
                  {agent.name}
                </Typography>
                <Box>
                  {access.editAiAgentAccess && (
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => onEdit(agent)}
                      sx={{ mr: 1, textTransform: "none", fontWeight: 700, borderRadius: "9px" }}
                    >
                      Edit
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    onClick={closeDetail}
                    sx={{ textTransform: "none", fontWeight: 700, borderRadius: "9px" }}
                  >
                    Back to List
                  </Button>
                </Box>
              </Box>

              <Typography variant="h6" color="textSecondary" gutterBottom>
                {agent.title}
              </Typography>

              {/* Agent Image */}
              {agent.imageUrl && (
                <Box mb={3} display="flex" justifyContent="center">
                  <img
                    src={agent.imageUrl}
                    alt={agent.name}
                    style={{
                      width: '120px',
                      height: '120px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: `3px solid ${agent.primaryColor || '#1976D2'}`,
                      backgroundColor: agent.backgroundColor || '#F5F5F5'
                    }}
                  />
                </Box>
              )}

              <Box mb={3}>
                <Typography variant="body1" paragraph>
                  {agent.shortDescription}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {agent.longDescription}
                </Typography>
              </Box>

              <Box mb={3}>
                <Typography variant="subtitle1" gutterBottom>
                  Specialties
                </Typography>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {agent.specialties?.map((specialty, index) => (
                    <Chip
                      key={index}
                      label={specialty}
                      color="primary"
                      variant="outlined"
                      style={{
                        borderColor: agent.primaryColor || '#1976D2',
                        color: agent.primaryColor || '#1976D2'
                      }}
                    />
                  ))}
                </div>
              </Box>

              <Box mb={3}>
                <Typography variant="subtitle1" gutterBottom>
                  Custom Instructions
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, backgroundColor: "var(--cms-surface-muted)" }}
                >
                  <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
                    {agent.customInstructions}
                  </Typography>
                </Paper>
              </Box>

              {/* Color Theme Display */}
              {(agent.primaryColor || agent.backgroundColor) && (
                <Box mb={3}>
                  <Typography variant="subtitle1" gutterBottom>
                    Color Theme
                  </Typography>
                  <Box display="flex" gap={2} alignItems="center">
                    {agent.primaryColor && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" color="textSecondary">
                          Primary:
                        </Typography>
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            backgroundColor: agent.primaryColor,
                            border: "1px solid var(--cms-border)",
                          }}
                        />
                        <Typography variant="caption" color="textSecondary">
                          {agent.primaryColor}
                        </Typography>
                      </Box>
                    )}
                    {agent.backgroundColor && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" color="textSecondary">
                          Background:
                        </Typography>
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            backgroundColor: agent.backgroundColor,
                            border: "1px solid var(--cms-border)",
                          }}
                        />
                        <Typography variant="caption" color="textSecondary">
                          {agent.backgroundColor}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {/* Voice Settings */}
              <Box mb={3}>
                <Typography variant="subtitle1" gutterBottom>
                  Voice Settings
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                  <Chip
                    label={(agent.voiceLocked === 1 || agent.voiceLocked === true) ? "Voice Locked" : "Voice Not Locked"}
                    color={(agent.voiceLocked === 1 || agent.voiceLocked === true) ? "warning" : "default"}
                    variant="outlined"
                  />
                  {agent.defaultVoice && (
                    <Chip
                      label={`Default: ${agent.defaultVoice}`}
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="textSecondary">
                    Status
                  </Typography>
                  <Typography variant="body2">
                    <Chip
                      label={agent.isActive ? "Active" : "Inactive"}
                      className={`status-badge ${
                        agent.isActive ? "status-active" : "status-inactive"
                      }`}
                    />
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="textSecondary">
                    Sort Order
                  </Typography>
                  <Typography variant="body2">{agent.sortOrder}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="textSecondary">
                    Created
                  </Typography>
                  <Typography variant="body2">
                    {new Date(agent.createdAt).toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="textSecondary">
                    Updated
                  </Typography>
                  <Typography variant="body2">
                    {new Date(agent.updatedAt).toLocaleDateString()}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Agent Stats & Quick Actions */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Stats
              </Typography>
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">
                  Knowledge Base Items
                </Typography>
                <Typography variant="h4">{kbCount}</Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">
                  Status
                </Typography>
                <Typography variant="body1">
                  {agent.isActive ? "🟢 Active" : "🔴 Inactive"}
                </Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">
                  Specialties Count
                </Typography>
                <Typography variant="body1">{agent.specialties?.length || 0}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <KnowledgeBasePanel agentId={agent.id} access={access} onCountChange={setKbCount} />
      </Grid>
    </div>
  )
}

export default AiAgentDetail
